import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ConversationDataObject, Message, TextingState } from '@/types/TTextingPlatform';

const initialState: TextingState = {
	conversations: {},
	conversationCursor: null,
	hasMoreConversations: false,
	isLoadingMore: false,
	selectedClient: '',
};

const textingSlice = createSlice({
	name: 'textingPlatform',
	initialState,
	reducers: {
		setConversations: (
			state,
			action: PayloadAction<{
				conversations?: ConversationDataObject;
				nextCursor?: string | null;
				hasMore?: boolean;
			}>,
		) => {
			const payload = action.payload || {};
			state.conversations = payload.conversations || {};
			state.conversationCursor = payload.nextCursor || null;
			state.hasMoreConversations = payload.hasMore || false;
		},

		addMoreConversations: (
			state,
			action: PayloadAction<{
				conversations?: ConversationDataObject;
				nextCursor?: string | null;
				hasMore?: boolean;
			}>,
		) => {
			const payload = action.payload || {};
			if (payload.conversations && typeof payload.conversations === 'object') {
				state.conversations = { ...state.conversations, ...payload.conversations };
			}
			state.conversationCursor = payload.nextCursor || null;
			state.hasMoreConversations = payload.hasMore || false;
		},

		addMoreMessages: (
			state,
			action: PayloadAction<{
				phoneNumber?: string;
				messages?: Message[];
				hasMore?: boolean;
			}>,
		) => {
			const payload = action.payload || {};
			const { phoneNumber, messages = [], hasMore = false } = payload;

			if (!phoneNumber || !state.conversations || !state.conversations[phoneNumber]) {
				return;
			}

			const conversation = state.conversations[phoneNumber];
			if (conversation && Array.isArray(messages)) {
				conversation.messages = [...messages, ...(conversation.messages || [])];
				conversation.hasMoreMessages = hasMore;
				if (messages.length > 0 && messages[0]?.timestamp) {
					conversation.oldestMessageCursor = messages[0].timestamp;
				}
			}
		},

		addNewMessage: (
			state,
			action: PayloadAction<{
				phoneNumber?: string;
				message?: Message;
			}>,
		) => {
			const payload = action.payload || {};
			const { phoneNumber, message } = payload;

			if (!phoneNumber || !message || !message.from || !message.to) {
				return;
			}

			state.conversations = state.conversations || {};

			if (state.conversations[phoneNumber]) {
				const conversation = state.conversations[phoneNumber];
				conversation.messages = conversation.messages || [];
				conversation.messages.push(message);
			} else {
				state.conversations[phoneNumber] = {
					client_number: phoneNumber,
					ai_receptionist_number: message.from === phoneNumber ? message.to : message.from,
					messages: [message],
					automatic_ai_reply: false,
				};
			}
		},

		setSelectedClient: (state, action: PayloadAction<string>) => {
			state.selectedClient = action.payload || '';
		},

		setLoadingMore: (state, action: PayloadAction<boolean>) => {
			state.isLoadingMore = Boolean(action.payload);
		},

		resetState: (state) => {
			state.conversations = {};
			state.conversationCursor = null;
			state.hasMoreConversations = false;
			state.isLoadingMore = false;
			state.selectedClient = '';
		},
	},
});

export const {
	setConversations,
	addMoreConversations,
	addMoreMessages,
	addNewMessage,
	setSelectedClient,
	setLoadingMore,
	resetState,
} = textingSlice.actions;

export default textingSlice.reducer;
